import React, {useCallback, useEffect, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import PresentationView from '../../components/presentationView/PresentationView';
import {useDebouncePresentationState} from '../../hooks/useDebouncePresentationState';
import {usePresentationParams} from '../../hooks/usePresentationParams';
import {getPresentationProtection, isPresentationOwner} from '../../service/presentation.service';
import {findPresentationByIdAction} from '../../store/actions/dashboard.actions';
import {desktopViewSelector} from '../../store/selectors/layout.selectors';
import {loadedPresentationSelector, presentationStatusSelector} from '../../store/selectors/presentation.selectors';
import {loggedInUserSelector} from '../../store/selectors/auth.selector';
import BaseLayout from '../layout/BaseLayout';
import {ProtectionInfo, ProtectionType} from '../../incase-domain/presentationProtection';
import IdentificationModal from '../../components/presentationView/parts/components/IdentificationModal';
import UserAuthModal from '../../components/presentationView/parts/components/UserAuthModal';
import {hasCommonElements} from '../../util/array';
import {GuestCredentials} from '../../components/presentationView/parts/components/GuestModal';

function PresentationPage(): JSX.Element {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {presentationId, params} = usePresentationParams();
    const loadedPresentation = useSelector(loadedPresentationSelector);
    const presentationStatus = useSelector(presentationStatusSelector);
    const isDesktopViewSelected = useSelector(desktopViewSelector);
    const loggedInUser = useSelector(loggedInUserSelector);
    const [protection, setProtection] = useState<ProtectionInfo>();
    const [credential, setCredential] = useState<GuestCredentials>();

    // TODO - ovo je brzi fix za protekciju koja bi uvijek trebala vracati rezultat (private ako ne postoji) - trenutno vrati 404
    const [notFound, setNotFound] = useState(false);

    const normalizeProtectionTypes = (types: string[]): ProtectionType[] => {
        return types.filter((type): type is ProtectionType => Object.values(ProtectionType).includes(type as ProtectionType));
    };

    useEffect(() => {
        (async () => {
            if (!presentationId) {
                navigate('/');
                return;
            }
            if (loadedPresentation?._id === presentationId) return;

            const isOwner = await isPresentationOwner(presentationId);
            if (isOwner) {
                dispatch(findPresentationByIdAction(presentationId, undefined, params));
                return;
            }

            const protection = await getPresentationProtection(presentationId);

            if (protection === undefined) {
                setNotFound(true);
            }

            const protectionTypes = normalizeProtectionTypes(protection.types || []);
            setProtection({...protection, types: protectionTypes});

            if (protectionTypes.includes(ProtectionType.GUEST) && protectionTypes.length === 1) {
                dispatch(findPresentationByIdAction(presentationId, undefined, params));
            }

            const externalAuthProtections = [ProtectionType.EMEDIKUS, ProtectionType.AAI_EDU, ProtectionType.ZOOM];
            if (loggedInUser && hasCommonElements(protectionTypes, externalAuthProtections)) {
                dispatch(findPresentationByIdAction(presentationId, undefined, params));
            }
        })();
    }, [dispatch, presentationId, params, loadedPresentation?._id, navigate, loggedInUser]);

    const findPresentation = useCallback(
        (cr?: GuestCredentials): void => {
            setCredential(cr);
            if (presentationId) dispatch(findPresentationByIdAction(presentationId, cr || credential, params));
        },
        [credential, dispatch, params, presentationId]
    );

    const isPresFound = !presentationStatus.found && presentationStatus.id === presentationId;
    if (isPresFound || !presentationId || notFound) {
        navigate('/');
    }

    return (
        <BaseLayout hasPresActions>
            <>
                <div className={`p-player ${isDesktopViewSelected ? 'p-player--desktop' : ''}`}>
                    <ErrorBoundary
                        FallbackComponent={() => {
                            toast.warning(t('playerPage.warning'), {toastId: 'player-warning'});
                            return null;
                        }}
                        onError={useDebouncePresentationState}
                    >
                        <PresentationView />
                    </ErrorBoundary>
                </div>

                <UserAuthModal
                    protection={{
                        protectionTypes: protection?.types || [],
                        identificationLabel: protection?.identificationLabel,
                    }}
                    findPresentation={findPresentation}
                />

                <IdentificationModal />
            </>
        </BaseLayout>
    );
}

export default PresentationPage;
